import { ActAsSession } from "core/types/act-as.types";

// Sessione act-as attiva. Vive FUORI da React di proposito: e' l'interceptor axios
// (App.tsx) a doverla leggere al momento della richiesta. Se il valore stesse solo in
// uno stato React, l'interceptor userebbe quello catturato nella closure al momento
// della registrazione e la delega diventerebbe "fantasma" (attiva a schermo, assente
// nelle chiamate, o viceversa).
//
// Persistita in sessionStorage e non in localStorage: la delega e' legata alla scheda
// del browser in cui l'operatore l'ha aperta e non si propaga alle altre schede.

export const ACT_AS_HEADER = "X-Act-As";

const STORAGE_KEY = "act_as_session";

type Listener = (session?: ActAsSession) => void;

let current: ActAsSession | undefined;
let hydrated = false;
const listeners = new Set<Listener>();

// Normalmente la scadenza e' gia' un ISO UTC ricostruito da `expires_in`
// (core/api/act-as/act-as.helpers.ts). Restano interpretabili anche le forme naive che
// arriverebbero da un backend senza `expires_in` ("2026-09-17 15:04:05.123456").
// Ritorna NaN se non e' interpretabile.
export const parseActAsExpiry = (value?: string): number => {
  if (!value) return NaN;
  const direct = new Date(value).getTime();
  if (!Number.isNaN(direct)) return direct;
  return new Date(
    value.replace(" ", "T").replace(/(\.\d{3})\d+$/, "$1")
  ).getTime();
};

const isExpired = (session: ActAsSession): boolean => {
  const expiresAt = parseActAsExpiry(session.expiresAt);
  // Scadenza illeggibile: NON si butta via la sessione. La scadenza che conta e' quella
  // in act_as_sessions, verificata a ogni richiesta; qui si evita solo di mandare un
  // header che il server rifiuterebbe di sicuro.
  if (Number.isNaN(expiresAt)) return false;
  return expiresAt <= Date.now();
};

const notify = () => {
  listeners.forEach((listener) => listener(current));
};

const hydrate = () => {
  hydrated = true;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as ActAsSession;
    if (!parsed?.sessionId || !parsed?.subject?.id || isExpired(parsed)) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    current = parsed;
  } catch (error) {
    // Valore corrotto: meglio nessuna delega che una delega non leggibile.
    console.error(error);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
  }
};

export const getActAsSession = (): ActAsSession | undefined => {
  if (!hydrated) hydrate();
  if (current && isExpired(current)) {
    // La scadenza e' anche lato server: qui si evita solo di continuare a mandare
    // un header che il backend rifiuterebbe con 403.
    setActAsSession(undefined);
  }
  return current;
};

export const setActAsSession = (session?: ActAsSession) => {
  hydrated = true;
  current = session;
  try {
    if (session) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    console.error(error);
  }
  notify();
};

export const clearActAsSession = () => setActAsSession(undefined);

// Usata dall'interceptor: valore dell'header, o undefined se non c'e' delega attiva.
export const getActAsSessionId = (): string | undefined =>
  getActAsSession()?.sessionId;

export const subscribeActAsSession = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
